"use client";

import { useNow } from "@/hooks/useNow";
import type { EventSource } from "@/types/discover";
import { isEventEnded, isRegistrationDeadlinePassed } from "@/utils/eventCta";
import { AlertTriangle } from "lucide-react";
import { useTranslations } from "next-intl";

/**
 * Cancelled, archived, ended, and deadline-passed banners. Resolved in the
 * browser because the page's HTML may be cached; see useNow.
 */
export default function EventStatusBanners({ event }: { event: EventSource }) {
  const now = useNow();
  const hasEventEnded = isEventEnded(event, now);
  const hasRegistrationDeadlinePassed = isRegistrationDeadlinePassed(
    event,
    now,
  );

  return (
    <>
      {event.cancelled && (
        <Banner translationKey="eventInfo.eventCancelledBanner" />
      )}
      {event.archived && (
        <Banner translationKey="eventInfo.eventArchivedBanner" />
      )}
      {hasEventEnded && !event.cancelled && (
        <Banner translationKey="eventInfo.eventEndedBanner" />
      )}
      {hasRegistrationDeadlinePassed &&
        !hasEventEnded &&
        !event.archived &&
        !event.cancelled && (
          <Banner translationKey="eventInfo.registrationDeadlinePassedBanner" />
        )}
    </>
  );
}

function Banner({ translationKey }: { translationKey: string }) {
  const t = useTranslations();
  return (
    <div
      data-testid="event-status-banner"
      data-translation-key={translationKey}
      className="rounded-md bg-destructive text-white px-3 py-2 xxs:px-4 xxs:py-2.5 flex items-center justify-center gap-1.5 xxs:gap-2"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
      <p className="text-sm leading-5 text-center">{t(translationKey)}</p>
    </div>
  );
}
